import { useEffect, useState } from "react";
import { loadRDKit, moleculeSvg, reactionSvgs } from "../../chem/rdkit";
import { openChemPanel } from "../../chem/panelStore";
import { useLoroSelector } from "../../sync/hooks";
import type { BlockComponentProps } from "../registry";
import { LoroTextInput, bodyMap } from "../textField";
import { useT } from "../../i18n";

/** Chemistry block (spec §7.4.8, §8.9): RDKit.js inline preview from `molfile`; Edit opens the panel. */
export function ChemistryBlock({ handle, treeId, node, readOnly, onFocus }: BlockComponentProps) {
  const t = useT();
  const body = bodyMap(node);
  const state = useLoroSelector(
    handle.doc,
    () => ({ kind: String(body?.get("kind") ?? "molecule"), molfile: String(body?.get("molfile") ?? "") }),
    (a, b) => a.kind === b.kind && a.molfile === b.molfile,
  );
  const [svg, setSvg] = useState<{ single?: string; reactants?: string[]; products?: string[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!state?.molfile.trim()) return;
    let stop = false;
    void loadRDKit()
      .then((rdkit) => {
        if (stop) return;
        if (state.kind === "reaction") setSvg(reactionSvgs(rdkit, state.molfile));
        else setSvg({ single: moleculeSvg(rdkit, state.molfile) ?? undefined });
        setErr(null);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
    return () => {
      stop = true;
    };
  }, [state?.kind, state?.molfile]);

  if (!body || !state) return null;
  const shown = state.molfile.trim() ? svg : null;
  return (
    <div className="rounded-md border border-neutral-200 p-2.5 dark:border-neutral-800" data-testid="chemistry-block" data-kind={state.kind} data-fill-column onFocus={onFocus}>
      <div className="flex items-center gap-2 text-xs text-neutral-500">
        <span>{t(state.kind === "reaction" ? "chem.kind.reaction" : "chem.kind.molecule")}</span>
        <div className="grow" />
        {!readOnly && (
          <button className="underline" data-testid="chem-edit" onClick={() => openChemPanel({ handle, treeId, body })}>
            {t("chem.edit")}
          </button>
        )}
      </div>
      {/* RDKit.js SVG is generated locally from the molfile (no server content) */}
      {shown?.single && <div className="[&_svg]:max-w-full" data-testid="chem-preview" data-fill-surface data-fill-center data-fill-drawing dangerouslySetInnerHTML={{ __html: shown.single }} />}
      {shown && !shown.single && (shown.reactants?.length || shown.products?.length) ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="chem-preview" data-fill-surface data-fill-center>
          {shown.reactants?.map((s, i) => <span key={`r${i}`} dangerouslySetInnerHTML={{ __html: s }} />)}
          <span className="text-2xl">→</span>
          {shown.products?.map((s, i) => <span key={`p${i}`} dangerouslySetInnerHTML={{ __html: s }} />)}
        </div>
      ) : null}
      {!state.molfile.trim() && <div className="text-sm text-neutral-500">{readOnly ? t("chem.empty") : t("chem.emptyEdit")}</div>}
      {err && <div className="text-xs text-red-600">{err}</div>}
      <LoroTextInput handle={handle} map={body} field="caption" placeholder={t("media.caption")} readOnly={readOnly} testId="media-caption" />
    </div>
  );
}
